import { Disclosure, Menu, Transition } from '@headlessui/react';
import { AcademicCapIcon, BellAlertIcon, ChevronDownIcon, ClockIcon, DocumentChartBarIcon, HomeIcon, NewspaperIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Bars3Icon } from '@heroicons/react/24/solid';
import { Feature } from 'admin/src/enums';
import DomainLogo from 'pollworker/src/components/DomainLogo';
import ElectionList from 'pollworker/src/components/ElectionList';
import { getEmployeeImage } from 'pollworker/src/fetchers';
import { useAppSelector, useIsFeatureFlagEnabled } from 'pollworker/src/hooks';
import { useLogOut } from 'pollworker/src/hooks/useLogOut';
import { selectCurrentUserIsManagerInElection, selectPendingResponseCount } from 'pollworker/src/state/schedule';
import { selectNeedsActionCount } from 'pollworker/src/state/training';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Flexor } from 'shared/src/components';
import { HiddenLink, ScreenReaderOnly } from 'shared/src/components/Accessibility';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'shared/src/components/ui/new';
import { cn } from 'shared/src/utils';
import { Link, useLocation } from 'wouter';

export default function HeaderAuthed() {
  const timeclockEnabled = useIsFeatureFlagEnabled('pollworker-timeclock');
  const lmsEnabled = useIsFeatureFlagEnabled(null, Feature.POLLWORKER_LMS);
  const currentUserIsManagerForElection = useAppSelector(selectCurrentUserIsManagerInElection);
  const currentUser = useAppSelector((state) => state.currentPollworkerUser);
  const currentCustomer = useAppSelector((state) => state.currentCustomer);
  const selectedElectionId = useAppSelector((state) => state.election.electionId);
  const trainingNeedsActionCount = useAppSelector(selectNeedsActionCount);
  const pendingResponseCount = useAppSelector(selectPendingResponseCount);
  const [employeeImage, setEmployeeImage] = useState<string>();
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [location, navigate] = useLocation();
  const logOut = useLogOut();

  useEffect(() => {
    void location;

    if (!selectedElectionId) return;

    const url = new URL(window.location.href);
    const currentParam = url.searchParams.get('electionId');

    if (currentParam !== selectedElectionId) {
      url.searchParams.set('electionId', selectedElectionId);
      window.history.replaceState({}, '', url.toString());
    }
  }, [location, selectedElectionId]);

  useEffect(() => {
    if (!currentCustomer || !currentUser) return;

    getEmployeeImage(currentCustomer.Customer.Id, currentUser.EVUserId)
      .then((response) => {
        const reader = new FileReader();

        reader.onloadend = () => {
          reader.result && setEmployeeImage(`data:image/png;base64,${reader.result.toString().split(',').pop()}`);
        };

        reader.readAsDataURL(response);
      })
      .catch(() => {
        return null;
      });
  }, [currentCustomer, currentUser]);

  const tabs = useMemo(() => {
    return [
      { current: location === '/', icon: HomeIcon, name: 'Home', path: '/' },
      { current: location === '/news', icon: NewspaperIcon, name: 'News', path: '/news' },
      timeclockEnabled && currentUserIsManagerForElection
        ? {
            current: location.startsWith('/work_schedule') || location.endsWith('/timeclock'),
            icon: ClockIcon,
            name: 'Schedule',
            path: '',
            badge: pendingResponseCount,
            badgeColor: 'bg-primary',
            nestedPath: [
              { current: location.endsWith('/work_schedule'), name: 'Work Schedule', path: '/work_schedule', userHasAccess: true },
              { current: location.endsWith('/timeclock'), name: 'TimeClock', path: '/schedule/timeclock', userHasAccess: true },
            ],
          }
        : { current: location.startsWith('/work_schedule'), icon: ClockIcon, name: 'Schedule', path: '/work_schedule', badge: pendingResponseCount, badgeColor: 'bg-primary' },
      lmsEnabled
        ? {
            current: location.startsWith('/training') || location.startsWith('/classes'),
            icon: AcademicCapIcon,
            name: 'Training',
            path: '/training',
            badge: trainingNeedsActionCount,
            badgeColor: 'bg-amber-600',
          }
        : {
            current: location.startsWith('/training') || location.startsWith('/classes'),
            icon: AcademicCapIcon,
            name: 'Training',
            path: '',
            badge: trainingNeedsActionCount,
            badgeColor: 'bg-amber-600',
            nestedPath: [
              { current: location === '/classes/required', name: 'Required', path: '/classes/required', userHasAccess: true },
              { current: location === '/classes/scheduled', name: 'Scheduled', path: '/classes/scheduled', userHasAccess: true },
            ],
          },
      { current: location === '/documents', icon: DocumentChartBarIcon, name: 'Documents', path: '/documents' },
    ];
  }, [timeclockEnabled, lmsEnabled, currentUserIsManagerForElection, location, trainingNeedsActionCount, pendingResponseCount]);

  return (
    <>
      <HiddenLink href={'#main-nav'}>Skip to main navigation</HiddenLink>
      {currentUser?.Customer ? (
        <div className="px-4 lg:px-16 text-sm border-b border-gray-100 py-1 bg-white">
          <Flexor className="mx-auto max-w-7xl">
            <div>{currentUser.Customer.Name}</div>
            <div>
              {currentUser.FirstName} {currentUser.LastName}
            </div>
          </Flexor>
        </div>
      ) : null}
      <Disclosure as="nav" id="main-nav" className="bg-white shadow px-4 lg:px-16">
        {({ open, close }) => (
          <>
            <div className="mx-auto max-w-7xl py-2">
              <div className="relative flex h-16 justify-between">
                <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                  {/* Mobile menu button */}
                  <Disclosure.Button as={Button} variant="ghost" size="icon" className="mr-1.5">
                    {open ? (
                      <XMarkIcon className="block shrink-0 h-6 w-6" aria-hidden="true" />
                    ) : (
                      <span>
                        <Bars3Icon className="block shrink-0 h-6 w-6" aria-hidden="true" />
                        <ScreenReaderOnly>Open main menu</ScreenReaderOnly>
                      </span>
                    )}
                  </Disclosure.Button>
                  <DomainLogo authed />
                </div>
                <div className="flex flex-1 items-center justify-start sm:items-stretch sm:justify-start">
                  <div className="flex-shrink-0 items-center hidden lg:flex">
                    <DomainLogo authed />
                  </div>
                  <div className="hidden lg:ml-6 sm:flex space-x-2 lg:space-x-8">
                    {tabs.map((tab) =>
                      tab.nestedPath ? (
                        <span key={tab.name}>
                          <Menu>
                            <Menu.Button
                              key={tab.name}
                              className={cn(
                                tab.current ? 'border-primary text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                                'h-full inline-flex items-center border-b-2 pt-1 px-1 text-sm font-medium',
                              )}
                            >
                              {tab.name}
                              <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
                            </Menu.Button>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-200"
                              enterFrom="transform opacity-0 scale-50"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items key={tab.name} className="absolute z-10 w-auto origin-top rounded-md bg-white py-1 shadow-lg focus:outline-none">
                                {tab.nestedPath?.map((nestedTab) =>
                                  nestedTab.userHasAccess ? (
                                    <Menu.Item key={nestedTab.name}>
                                      {({ active }) => (
                                        <button
                                          type="button"
                                          key={nestedTab.name}
                                          className={cn(active ? 'bg-gray-100' : '', 'w-full text-left block px-4 py-2 text-sm text-gray-700')}
                                          onClick={() => {
                                            navigate(nestedTab.path);
                                          }}
                                        >
                                          {nestedTab.name}
                                        </button>
                                      )}
                                    </Menu.Item>
                                  ) : null,
                                )}
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        </span>
                      ) : (
                        <Link
                          key={tab.name}
                          href={tab.path}
                          className={cn(
                            tab.current ? 'border-primary text-gray-900' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                            'h-full inline-flex items-center border-b-2 pt-1 px-1 text-sm font-medium',
                          )}
                        >
                          {tab.name}
                          {tab.badge ? (
                            <span
                              className={`ml-1.5 inline-flex items-center justify-center rounded-full ${tab.badgeColor || 'bg-primary'} px-1.5 py-0.5 text-xs font-medium text-white`}
                            >
                              {tab.badge}
                            </span>
                          ) : null}
                        </Link>
                      ),
                    )}
                  </div>
                </div>
                <Flexor>
                  <div className="hidden sm:inline px-4">
                    <ElectionList />
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:pr-0">
                    <DropdownMenu>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-3 inline-flex">
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  className="relative flex rounded-full bg-white text-sm ring-4 ring-primary hover:ring-primary/60 hover:ring-4 focus:outline-none focus:ring-offset-2 focus:ring-primary"
                                >
                                  <ScreenReaderOnly>Open user menu,</ScreenReaderOnly>
                                  {employeeImage ? <img className="h-8 w-8 rounded-full" src={employeeImage} alt="Profile" /> : <UserCircleIcon className="h-8 w-8" />}
                                </button>
                              </DropdownMenuTrigger>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Profile and settings</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
                        <DropdownMenuItem onClick={logOut}>Sign out</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </Flexor>
              </div>
            </div>

            <Disclosure.Panel className="sm:hidden">
              <div className="space-y-1 pb-4 pt-2">
                {tabs.map((tab) => (
                  <div key={tab.path}>
                    {!tab.nestedPath ? (
                      <Disclosure.Button
                        as="a"
                        key={tab.path}
                        href={tab.path}
                        onClick={(event) => {
                          event.preventDefault();
                          close();
                          navigate(tab.path);
                        }}
                        className={cn(
                          tab.current
                            ? 'border-l-4 border-primary bg-primary/10 py-2 pl-3 pr-4 text-base font-medium text-primary/80'
                            : 'border-l-4 border-transparent py-2 pl-3 pr-4 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700',
                          'flex items-center justify-between',
                        )}
                      >
                        <span>{tab.name}</span>
                        {tab.badge ? (
                          <span className={`inline-flex items-center justify-center rounded-full ${tab.badgeColor || 'bg-primary'} px-1.5 py-0.5 text-xs font-medium text-white`}>
                            {tab.badge}
                          </span>
                        ) : null}
                      </Disclosure.Button>
                    ) : null}
                    {tab.nestedPath ? (
                      <span key={tab.path} className={cn('block border-l-4 border-transparent py-2 pl-3 pr-4 text-base font-medium text-gray-500')}>
                        {tab.name}
                      </span>
                    ) : null}
                    {tab.nestedPath?.map((nestedTab) => (
                      <div key={nestedTab.path}>
                        <Disclosure.Button
                          as="a"
                          key={nestedTab.path}
                          href={nestedTab.path}
                          onClick={(event) => {
                            event.preventDefault();
                            close();
                            navigate(nestedTab.path);
                          }}
                          className={cn(
                            nestedTab.current
                              ? 'block border-l-4 ml-8 border-primary bg-primary/10 py-2 pl-3 pr-4 text-base font-medium text-primary/80'
                              : 'block border-l-4 ml-8 border-transparent py-2 pl-3 pr-4 text-base font-medium text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700',
                          )}
                        >
                          {nestedTab.name}
                        </Disclosure.Button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
      {!bannerDismissed && (pendingResponseCount > 0 || trainingNeedsActionCount > 0) && (
        <div className="bg-primary/10 border-y border-primary/20 px-4 lg:px-16">
          <div className="mx-auto max-w-7xl py-3">
            <div className="flex items-center justify-between gap-x-4">
              <div className="flex items-center gap-x-2">
                <BellAlertIcon className="h-5 w-5 text-primary flex-shrink-0" />
                <p className="text-sm text-gray-700">
                  You have{' '}
                  {pendingResponseCount > 0 && (
                    <Link to="/work_schedule">
                      <strong className="text-white rounded-full inline-block px-1.5 scale-90 bg-primary">{pendingResponseCount}</strong>{' '}
                      {pendingResponseCount === 1 ? 'schedule' : 'schedules'} to respond to
                    </Link>
                  )}
                  {pendingResponseCount > 0 && trainingNeedsActionCount > 0 && ' and '}
                  {trainingNeedsActionCount > 0 && (
                    <Link to={lmsEnabled ? '/training' : '/classes/required'}>
                      <strong className="text-amber-100 rounded-full inline-block px-1.5 scale-90 bg-amber-600">{trainingNeedsActionCount}</strong> training{' '}
                      {trainingNeedsActionCount === 1 ? 'class' : 'classes'} to sign up for
                    </Link>
                  )}
                </p>
              </div>
              <button type="button" onClick={() => setBannerDismissed(true)} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
                <XMarkIcon className="h-5 w-5" />
                <ScreenReaderOnly>Dismiss</ScreenReaderOnly>
              </button>
            </div>
          </div>
        </div>
      )}
      <Flexor className="hidden mx-auto px-16 mb-10">
        <DomainLogo authed />
        <div>
          <div className="sm:hidden">
            <label htmlFor="tabs" className="sr-only">
              Select a tab
            </label>
            <select
              id="tabs"
              name="tabs"
              className="block w-full rounded-md border-gray-300 focus:border-primary focus:ring-primary"
              defaultValue={tabs.find((tab) => tab.current)?.name}
            >
              {tabs.map((tab) => (
                <option key={tab.name}>{tab.name}</option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <div className="">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    type="button"
                    key={tab.name}
                    onClick={() => navigate(tab.path)}
                    className={cn(
                      tab.current ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                      'group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium',
                    )}
                    aria-current={tab.current ? 'page' : undefined}
                  >
                    <tab.icon className={cn(tab.current ? 'text-primary' : 'text-gray-400 group-hover:text-gray-500', '-ml-0.5 mr-2 h-5 w-5')} aria-hidden="true" />
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </Flexor>
      <div className="sm:hidden flex justify-start bg-white shadow p-4">
        <ElectionList />
      </div>
    </>
  );
}
